import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Document, Page, pdfjs } from 'react-pdf';
import './ReportPage.css';

// Correct worker setup
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ReportPage = () => {
    const reduxReportUrl = useSelector((state) => state.quiz.reportUrl);
    const loading = useSelector((state) => state.quiz.loading);

    const [reportUrl, setReportUrl] = useState(null);
    const [downloadDisabled, setDownloadDisabled] = useState(false);
    const [expiresAt, setExpiresAt] = useState(null);
    const [expiresString, setExpiresString] = useState('');
    const [numPages, setNumPages] = useState(null);

    // Set report URL from Redux or localStorage
    useEffect(() => {
        if (reduxReportUrl && reduxReportUrl !== 'initial') {
            localStorage.removeItem('reportUrl');
            localStorage.removeItem('reportExpiryTime');
            setReportUrl(reduxReportUrl);
            localStorage.setItem('reportUrl', reduxReportUrl);
        } else if (!loading) {
            const storedUrl = localStorage.getItem('reportUrl');
            if (storedUrl) {
                setReportUrl(storedUrl);
            } else {
                window.location.href = '/skin-quiz';
            }
        }
    }, [reduxReportUrl, loading]);

    // Expiration logic
    useEffect(() => {
        if (!reportUrl) return;

        let timer;
        const expiryTimeInStorage = localStorage.getItem('reportExpiryTime');
        const now = Date.now();

        const setupExpiry = (expiryTime) => {
            setExpiresAt(expiryTime);
            const expirationDate = new Date(expiryTime);
            setExpiresString(
                expirationDate.toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })
            );
            timer = setTimeout(() => {
                setDownloadDisabled(true);
                localStorage.removeItem('reportExpiryTime');
                localStorage.removeItem('reportUrl');
            }, expiryTime - now);
        };

        if (expiryTimeInStorage) {
            const remaining = parseInt(expiryTimeInStorage, 10) - now;
            if (remaining > 0) setupExpiry(parseInt(expiryTimeInStorage, 10));
            else setDownloadDisabled(true);
        } else if (reduxReportUrl && reduxReportUrl !== 'initial') {
            const newExpiry = now + 60 * 60 * 1000; // 60 min
            localStorage.setItem('reportExpiryTime', newExpiry);
            setupExpiry(newExpiry);
        }

        return () => clearTimeout(timer);
    }, [reportUrl, reduxReportUrl]);

    const handleDownload = async () => {
        if (downloadDisabled || !reportUrl) return;

        try {
            const response = await fetch(reportUrl);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = 'Clear-and-Bare-Skincare-Report.pdf';
            document.body.appendChild(link);
            link.target = '_blank';
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            const link = document.createElement('a');
            link.href = reportUrl;
            link.download = 'Clear-and-Bare-Skincare-Report.pdf';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    if (loading || !reportUrl) {
        return (
            <div className="report-page loading">
                <div className="loading-spinner"></div>
                <h2>{loading ? 'Generating Your Personalized Report...' : 'Loading Your Report...'}</h2>
            </div>
        );
    }

    return (
        <div className="report-page">
            <div className="report-container">
                <div className="report-header">
                    <h1>Your Personalized Skincare Report</h1>
                    <div className="expiry-notice">
                        <span className="warning-icon">⚠️</span>
                        <p>This secure link will expire at: <strong>{expiresString}</strong></p>
                        <p className="expiry-text">For your privacy and security, this link will no longer work after 60 minutes.</p>
                    </div>
                </div>

                <div className="report-content">
                    <div className="pdf-viewer">
                        <Document
                            file={reportUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            className="report-pdf-document"
                        >
                            {Array.from(new Array(numPages), (_, index) => (
                                <Page
                                    key={`page_${index + 1}`}
                                    pageNumber={index + 1}
                                    width={900}
                                    className="report-pdf-page"
                                    renderAnnotationLayer={false}
                                    renderTextLayer={false}
                                />
                            ))}
                        </Document>
                    </div>

                    <div className="report-actions">
                        <button
                            className="btn-primary download-btn"
                            onClick={handleDownload}
                            disabled={downloadDisabled}
                        >
                            📥 Download Your Report
                        </button>

                        <div className="report-info">
                            <h3>What's in Your Report:</h3>
                            <ul>
                                <li>✓ Detailed skin type analysis</li>
                                <li>✓ Personalized morning & evening routines</li>
                                <li>✓ Product recommendations for your skin type</li>
                                <li>✓ Diet tips for healthy skin</li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="security-notice">
                    <p><strong>Privacy Notice:</strong> Your report contains personalized recommendations based on your responses.
                        For your privacy, this secure link will automatically expire in 60 minutes.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ReportPage;
